import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, LogOut, Lock, Mail, CreditCard, Inbox, ArrowLeft, RefreshCw,
} from "lucide-react";
import { brand } from "@/config/brand";
import { platformName } from "@/config/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "smartweb_admin_token";

const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtAmount = (cents, currency) => {
  if (cents == null) return "—";
  const sym = (currency || "usd").toUpperCase() === "USD" ? "$" : `${(currency || "").toUpperCase()} `;
  return `${sym}${(cents / 100).toFixed(2)}`;
};

/* ---------- Login ---------- */
function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-base px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm rounded-[10px] border border-black/10 bg-white p-8 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.4)]"
      >
        <img src={brand.logo_url} alt={brand.brand_name} className="mx-auto mb-8 h-10 w-auto object-contain" />
        <div className="mb-6 flex items-center justify-center gap-2 text-sm font-semibold text-black/70">
          <Lock size={15} /> Admin sign in
        </div>

        <label htmlFor="admin-email" className="mb-1.5 block text-xs font-medium text-black/55">Email</label>
        <input
          id="admin-email"
          data-testid="admin-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@business.com"
          className="mb-4 w-full rounded-[10px] border border-black/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-black"
        />
        <label htmlFor="admin-password" className="mb-1.5 block text-xs font-medium text-black/55">Password</label>
        <input
          id="admin-password"
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mb-4 w-full rounded-[10px] border border-black/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-black"
        />

        {error && (
          <p data-testid="admin-login-error" className="mb-4 rounded-[8px] bg-black/[0.04] px-3 py-2 text-xs font-medium text-red-600">
            {error}
          </p>
        )}

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-black py-3.5 text-sm font-semibold text-white transition-colors hover:bg-black/90 disabled:opacity-60"
        >
          {loading && <Loader2 size={15} className="animate-spin" />}
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-xs font-medium text-black/45 hover:text-black">
          <ArrowLeft size={13} /> Back to website
        </Link>
      </form>
    </div>
  );
}

/* ---------- Dashboard ---------- */
function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("messages");
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers: authHeader() }),
        axios.get(`${API}/admin/payments`, { headers: authHeader() }),
      ]);
      setContacts(c.data);
      setPayments(p.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const paidCount = payments.filter((p) => p.payment_status === "paid").length;

  const TABS = [
    { id: "messages", label: "Messages", icon: Mail, count: contacts.length },
    { id: "payments", label: "Payments", icon: CreditCard, count: payments.length },
  ];

  return (
    <div className="min-h-screen bg-base">
      {/* header */}
      <header className="sticky top-0 z-10 border-b border-white/10 bg-black">
        <div className="mx-auto flex h-[72px] max-w-[1200px] items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-4">
            <img src={brand.logo_url} alt={brand.brand_name} className="h-8 w-auto object-contain" />
            <span className="hidden text-sm font-medium text-white/50 sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-white/50 sm:inline">{user?.email}</span>
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-white/20 text-white transition-colors hover:bg-white/10"
              aria-label="Refresh"
            >
              <RefreshCw size={15} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-[10px] border border-white/25 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 py-10 sm:px-8">
        {/* stat chips */}
        <div className="mb-8 grid grid-cols-3 gap-4">
          {[
            { label: "Messages", value: contacts.length },
            { label: "Transactions", value: payments.length },
            { label: "Paid", value: paidCount },
          ].map((s) => (
            <div key={s.label} className="rounded-[10px] border border-black/10 bg-white p-5">
              <div className="text-3xl font-black tracking-tight">{s.value}</div>
              <div className="mt-1 text-xs font-medium uppercase tracking-[0.14em] text-black/45">{s.label}</div>
            </div>
          ))}
        </div>

        {/* tabs */}
        <div className="mb-5 flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-[10px] px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === t.id ? "bg-black text-white" : "border border-black/15 text-black/70 hover:bg-black/[0.03]"
              }`}
            >
              <t.icon size={15} /> {t.label}
              <span className={`rounded-full px-2 py-0.5 text-[10px] ${tab === t.id ? "bg-white/20" : "bg-black/[0.06]"}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-[10px] border border-black/10 bg-white">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-black/50">
              <Loader2 size={18} className="animate-spin" /> Loading…
            </div>
          ) : tab === "messages" ? (
            contacts.length === 0 ? (
              <Empty icon={Inbox} text="No messages yet." />
            ) : (
              <div className="overflow-x-auto">
                <table data-testid="admin-messages-table" className="w-full min-w-[720px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-black/10 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-black/45">
                      <th className="px-5 py-3">Date</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Email</th>
                      <th className="px-5 py-3">Business</th>
                      <th className="px-5 py-3">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c, i) => (
                      <tr key={i} className="border-b border-black/[0.06] align-top">
                        <td className="whitespace-nowrap px-5 py-4 text-black/60">{fmtDate(c.created_at)}</td>
                        <td className="px-5 py-4 font-medium">{c.name}</td>
                        <td className="px-5 py-4 text-black/70">{c.email}</td>
                        <td className="px-5 py-4 text-black/70">{c.business || "—"}</td>
                        <td className="max-w-md px-5 py-4 text-black/70">{c.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : payments.length === 0 ? (
            <Empty icon={CreditCard} text="No payments yet." />
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="admin-payments-table" className="w-full min-w-[760px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-black/45">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Plan</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Session</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={i} className="border-b border-black/[0.06]">
                      <td className="whitespace-nowrap px-5 py-4 text-black/60">{fmtDate(p.created_at)}</td>
                      <td className="px-5 py-4 font-medium">{platformName(p.lookup_key || "—")}</td>
                      <td className="px-5 py-4">{fmtAmount(p.amount, p.currency)}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium ${
                            p.payment_status === "paid"
                              ? "bg-black text-white"
                              : "bg-black/[0.06] text-black/60"
                          }`}
                        >
                          {p.payment_status}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-mono text-[11px] text-black/40">{(p.session_id || "").slice(0, 22)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const Empty = ({ icon: Icon, text }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-20 text-black/40">
    <Icon size={28} />
    <p className="text-sm font-medium">{text}</p>
  </div>
);

/* ---------- Page ---------- */
export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined = checking, null = logged out

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUser(data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      });
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base">
        <Loader2 className="animate-spin text-black" size={28} />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginView onSuccess={setUser} />;
}
